import { isSet } from "../utils/isSet";
import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import ClassNames from 'embla-carousel-class-names';
import Fade from 'embla-carousel-fade'
import { addPrevNextBtnsClickHandlers } from './emblaCarouselControls';
import { addDotBtnsAndClickHandlers } from './emblaCarouselPagination';
import { setupProgress } from './emblaCarouselProgress';
import { setupLazyLoadImage } from './emblaCarouselLazyLoad';
import { addThumbBtnsClickHandlers, addToggleThumbBtnsActive } from '../functions/emblaCarouselThumbButtons';

function initEmblaCarousels(){

    function initMediaContentCarousel(){
        const mediaContentCarouselNodes = document.querySelectorAll('.block--media-content--stylised .embla--js');
        if(!isSet(mediaContentCarouselNodes)) return;

        mediaContentCarouselNodes.forEach((mediaContentCarouselNode) => {
            const mediaContentViewportNode = mediaContentCarouselNode.querySelector('.embla__viewport');
            if(!isSet(mediaContentViewportNode)) return;

            const mediaContentSlideNodes = mediaContentViewportNode.querySelectorAll('.embla__slide');
            if(!isSet(mediaContentSlideNodes) || mediaContentSlideNodes.length === 0) return;

            const dotsNode = mediaContentCarouselNode.querySelector('.embla__pagination');
    
            const mediaContentOptions = {
                loop: true
            }
    
            const mediaContentEmblaApi = EmblaCarousel(
                mediaContentViewportNode, 
                mediaContentOptions,
                [
                    Fade(),
                    Autoplay(
                        {
                            delay: 8000
                        }
                    )
                ]
            );

            if(isSet(dotsNode)){
                const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(
                    mediaContentEmblaApi,
                    dotsNode
                );

                mediaContentEmblaApi.on('destroy', removeDotBtnsAndClickHandlers);
            }

            const loadImagesInView = setupLazyLoadImage(mediaContentEmblaApi);

            function resetTimerOnSlidesInView(emblaApi){
                const resetTimerInViewAndDestroyIfDone = (emblaApi) => {
                    emblaApi.plugins().autoplay.reset();
                }
                return resetTimerInViewAndDestroyIfDone
            }

            const resetTimerInView = resetTimerOnSlidesInView(mediaContentEmblaApi);

            mediaContentEmblaApi
                .on('init', loadImagesInView)
                .on('reInit', loadImagesInView)
                .on('slidesInView', loadImagesInView)
                .on('slidesInView', resetTimerInView)

        });
    }
    initMediaContentCarousel();

    function initCoverCarousel(){
        const coverCarouselNodes = document.querySelectorAll('.block--cover-carousel .embla--js');
        if(!isSet(coverCarouselNodes)) return;

        coverCarouselNodes.forEach((coverCarouselNode) => {
            const coverViewportNode = coverCarouselNode.querySelector('.embla__viewport');
            if(!isSet(coverViewportNode)) return;

            const coverThumbViewportNode = coverCarouselNode.querySelector('.embla-thumbs__viewport');
            if(!isSet(coverThumbViewportNode)) return;

            const coverSlideNodes = coverViewportNode.querySelectorAll('.embla__slide');
            if(!isSet(coverSlideNodes) || coverSlideNodes.length === 0) return;

            const dotsNode = coverCarouselNode.querySelector('.embla__pagination');

            const coverOptions = {
                loop: true,
                watchDrag: false
            }

            const coverThumbOptions = {
                loop: true,
                watchDrag: false
            }
    
            const autoplayPlugin = Autoplay(
                { delay: 8000 },  // options
                undefined,        // no callback needed
                'autoplay'        // plugin name
            )

            const coverEmblaApi = EmblaCarousel(
                coverViewportNode, 
                coverOptions,
                [
                    Fade(),
                    ClassNames(),
                    autoplayPlugin
                ]
            );

            const coverEmblaThumbApi = EmblaCarousel(
                coverThumbViewportNode, 
                coverThumbOptions,
                [
                    ClassNames()
                ]
            );

            const loadImagesInView = setupLazyLoadImage(coverEmblaApi);

            const toggleActiveWhenScrollable = () => {
                const isScrollable = coverEmblaThumbApi.internalEngine().scrollSnaps.length > 1
                coverEmblaThumbApi.reInit({ active: isScrollable })
            }

            function resetTimerOnSlidesInView(emblaApi){
                const resetTimerInViewAndDestroyIfDone = (emblaApi) => {
                    emblaApi.plugins().autoplay.reset();
                }
                return resetTimerInViewAndDestroyIfDone
            }

            const resetTimerInView = resetTimerOnSlidesInView(coverEmblaApi);

            const removeThumbBtnsClickHandlers = addThumbBtnsClickHandlers(
                coverEmblaApi,
                coverEmblaThumbApi
            );

            const removeToggleThumbBtnsActive = addToggleThumbBtnsActive(
                coverEmblaApi,
                coverEmblaThumbApi
            );

            const removeProgressListeners = setupProgress(
                coverEmblaApi,
                coverCarouselNode,
                8000
            );

            if(isSet(dotsNode)){
                const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(
                    coverEmblaApi,
                    dotsNode
                );

                coverEmblaApi.on('destroy', removeDotBtnsAndClickHandlers);
            }

            coverEmblaApi
                .on('init', loadImagesInView)
                .on('reInit', loadImagesInView)
                .on('slidesInView', loadImagesInView)
                .on('slidesInView', resetTimerInView)
                .on('destroy', removeThumbBtnsClickHandlers)
                .on('destroy', removeToggleThumbBtnsActive)
                .on('destroy', removeProgressListeners)


            coverEmblaThumbApi
                .on('destroy', removeThumbBtnsClickHandlers)
                .on('destroy', removeToggleThumbBtnsActive)
                .on('resize', toggleActiveWhenScrollable)

            toggleActiveWhenScrollable();
        });
    }
    initCoverCarousel();


    function initLogoCarousel(){
        const logoCarouselNodes = document.querySelectorAll('.block--logo-carousel .embla--js');
        if(!isSet(logoCarouselNodes)) return;

        logoCarouselNodes.forEach((logoCarouselNode) => {
            const logoViewportNode = logoCarouselNode.querySelector('.embla__viewport');
            if(!isSet(logoViewportNode)) return;

            const logoSlideNodes = logoViewportNode.querySelectorAll('.embla__slide');
            if(!isSet(logoSlideNodes) || logoSlideNodes.length === 0) return;

            const dotsNode = logoCarouselNode.querySelector('.embla__pagination');

            const prevBtnNode = logoCarouselNode.querySelector('.embla__button--prev');
            const nextBtnNode = logoCarouselNode.querySelector('.embla__button--next');
    
            const logoOptions = {
                loop: true,
                align: 'start'
            }
    
            const logoEmblaApi = EmblaCarousel(
                logoViewportNode, 
                logoOptions,
                [
                    Autoplay(
                        {
                            delay: 4000
                        }
                    )
                ]
            );

            if(isSet(dotsNode)){
                const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(
                    logoEmblaApi,
                    dotsNode
                );

                logoEmblaApi.on('destroy', removeDotBtnsAndClickHandlers);
            }

            if(isSet(prevBtnNode) && isSet(nextBtnNode)){
                const removeLogoCarouselPrevNextClickHandlers = addPrevNextBtnsClickHandlers(
                    logoEmblaApi,
                    prevBtnNode,
                    nextBtnNode
                );

                logoEmblaApi.on('destroy', removeLogoCarouselPrevNextClickHandlers);
            }

            const loadImagesInView = setupLazyLoadImage(logoEmblaApi);

            const toggleActiveWhenScrollable = () => {
                const isScrollable = logoEmblaApi.internalEngine().scrollSnaps.length > 1
                logoEmblaApi.reInit({ watchDrag: isScrollable });
                if(isScrollable){
                    logoCarouselNode.classList.add('is-active');
                    logoCarouselNode.classList.remove('is-disabled');
                } else{
                    logoCarouselNode.classList.add('is-disabled');
                    logoCarouselNode.classList.remove('is-active');
                }
            }

            function resetTimerOnSlidesInView(emblaApi){
                const resetTimerInViewAndDestroyIfDone = (emblaApi) => {
                    emblaApi.plugins().autoplay.reset();
                }
                return resetTimerInViewAndDestroyIfDone
            }

            const resetTimerInView = resetTimerOnSlidesInView(logoEmblaApi);

            logoEmblaApi
                .on('init', loadImagesInView)
                .on('reInit', loadImagesInView)
                .on('slidesInView', loadImagesInView)
                .on('slidesInView', resetTimerInView)
                .on('resize', toggleActiveWhenScrollable)

            toggleActiveWhenScrollable();
        });
    }
    initLogoCarousel();
    document.arrive(".block--logo-carousel", () => {
        initLogoCarousel();
    });


    function initImageBlockCarousel(){
        const imageBlockCarouselNodes = document.querySelectorAll('.block--image .embla--js');
        if(!isSet(imageBlockCarouselNodes)) return;

        imageBlockCarouselNodes.forEach((imageBlockCarouselNode) => {
            const imageBlockViewportNode = imageBlockCarouselNode.querySelector('.embla__viewport');
            if(!isSet(imageBlockViewportNode)) return;

            const imageBlockSlideNodes = imageBlockViewportNode.querySelectorAll('.embla__slide');
            if(!isSet(imageBlockSlideNodes) || imageBlockSlideNodes.length === 0) return;

            const dotsNode = imageBlockCarouselNode.querySelector('.embla__pagination');
    
            const imageBlockOptions = {
                loop: true
            }
    
            const imageBlockEmblaApi = EmblaCarousel(
                imageBlockViewportNode, 
                imageBlockOptions,
                [
                    Fade(),
                    Autoplay(
                        {
                            delay: 8000
                        }
                    )
                ]
            );

            if(isSet(dotsNode)){
                const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(
                    imageBlockEmblaApi,
                    dotsNode
                );

                imageBlockEmblaApi.on('destroy', removeDotBtnsAndClickHandlers);
            }

            const loadImagesInView = setupLazyLoadImage(imageBlockEmblaApi);

            function resetTimerOnSlidesInView(emblaApi){
                const resetTimerInViewAndDestroyIfDone = (emblaApi) => {
                    emblaApi.plugins().autoplay.reset();
                }
                return resetTimerInViewAndDestroyIfDone
            }

            const resetTimerInView = resetTimerOnSlidesInView(imageBlockEmblaApi);

            imageBlockEmblaApi
                .on('init', loadImagesInView)
                .on('reInit', loadImagesInView)
                .on('slidesInView', loadImagesInView)
                .on('slidesInView', resetTimerInView)

        });
    }
    initImageBlockCarousel();


    function initMachineHeaderCarousel(){
        const machineHeaderCarouselNodes = document.querySelectorAll('.block--header--machine .embla--js');
        if(!isSet(machineHeaderCarouselNodes)) return;

        machineHeaderCarouselNodes.forEach((machineHeaderCarouselNode) => {
            const machineHeaderViewportNode = machineHeaderCarouselNode.querySelector('.embla__viewport');
            if(!isSet(machineHeaderViewportNode)) return;

            const machineHeaderSlideNodes = machineHeaderViewportNode.querySelectorAll('.embla__slide');
            if(!isSet(machineHeaderSlideNodes) || machineHeaderSlideNodes.length === 0) return;

            const dotsNode = machineHeaderCarouselNode.querySelector('.embla__pagination');
    
            const machineHeaderOptions = {
                loop: true
            }
    
            const machineHeaderEmblaApi = EmblaCarousel(
                machineHeaderViewportNode, 
                machineHeaderOptions,
                []
            );

            if(isSet(dotsNode)){
                const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(
                    machineHeaderEmblaApi,
                    dotsNode
                );

                machineHeaderEmblaApi.on('destroy', removeDotBtnsAndClickHandlers);
            }

            const loadImagesInView = setupLazyLoadImage(machineHeaderEmblaApi);

            machineHeaderEmblaApi
                .on('init', loadImagesInView)
                .on('reInit', loadImagesInView)
                .on('slidesInView', loadImagesInView)

        });
    }
    initMachineHeaderCarousel();

}
export { initEmblaCarousels };